import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { selectGameConfig } from "src/store/common"
import {
  selectCurrentPlayer,
  selectCurrentTeam,
  selectMetadataCells,
  selectMetadataUsers,
  selectMovements,
  selectResearchLevels,
  selectViewerId,
} from "src/store/game"
import { foodOutlookFor, foodStaffingFor } from "src/utils/foodOutlook"

import "./FoodOutlook.css"

/* What next round does to your food.

   18d found a game where food fell 5560 → 0 and starvation took 541 troops in one tick, and fixed
   the half of it that was an invisible standing order. The other road to the same famine is an
   army whose upkeep has outgrown its farms, and the HUD still showed only a falling number. Losses
   are proportional, so at zero food the whole army starves at once — there is no partial round of
   warning, which is why the warning has to come before.

   Verified against the server: predicted net +92, observed food delta +92 on the same tick. */
/* Green from nine in ten hands, red under six in ten, amber between — the same three the rest of
   the HUD uses for fine, watch it, and wrong. Exported so the thresholds can be pinned. */
export const staffingTone = (share) => (share >= 90 ? "ok" : share >= 60 ? "soso" : "bad")

const FoodOutlook = () => {
  const { t } = useTranslation()
  const gameConfig = useSelector(selectGameConfig)
  const cells = useSelector(selectMetadataCells)
  const userId = useSelector(selectViewerId)
  const team = useSelector(selectCurrentTeam)
  const race = useSelector(selectCurrentPlayer)?.race
  const researchLevels = useSelector(selectResearchLevels)
  const movements = useSelector(selectMovements)
  // Who is standing where, so a besieged farm's harvest is left out of the forecast the way the
  // server leaves it out of the round (2026-09-08).
  const users = useSelector(selectMetadataUsers)

  if (!gameConfig || !team) return null

  const { income, upkeep, net } = foodOutlookFor({
    gameConfig,
    cells,
    movements,
    users,
    userId,
    team,
    race,
    researchLevels,
  })

  // Nothing produced and nothing eaten is the opening state, and a chip reading "+0" there is
  // noise rather than information.
  if (income === 0 && upkeep === 0) return null

  const label = team === "human" ? t("game.food.food") : t("game.food.meat")
  // Fields without hands, as one number beside the net figure (owner, 2026-09-06: the bar and
  // its line were a paragraph in a chip; "the 93% can be a number directly, with colour"). The
  // share of the workers the farms need that are actually in them, coloured by how bad it is —
  // green is fine, amber is so-so, red is a harvest being left in the field. The sentence lives
  // on in the tooltip and in the food sheet, where there is room for it.
  const staffing = foodStaffingFor({ gameConfig, cells, userId, team })
  const hasFields = staffing.required > 0
  const short = hasFields && staffing.missing > 0
  const title = short
    ? t("game.food.staffingTitle", staffing)
    : t("game.food.outlookTitle", { income, label, upkeep }) + (net < 0 ? ` ${t("game.food.starvingWarning")}` : "")

  return (
    <div className="food-outlook-wrap" title={title}>
      <p className={`resource-extra food-outlook ${net < 0 ? "food-outlook-falling" : ""}`}>
        {t("game.food.perRound", { net: net >= 0 ? `+${net}` : net })}
        {hasFields && (
          <span
            className={`food-staffing-share food-staffing-${staffingTone(staffing.share)}`}
            aria-label={t("game.food.staffing", staffing)}
          >
            {staffing.share}%
          </span>
        )}
      </p>
    </div>
  )
}

export default FoodOutlook
